import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd-v5';
import InquiryHeader from './components/InquiryHeader';
import StorySection from './components/StorySection';
import TagsSection from './components/TagsSection';
import RequestedProducts from './components/RequestedProducts';
import ClosedInquiries from './components/ClosedInquiries';
import AddRolexInquiryModal from '../../../../../componentsV2/Rolex/AddRolexInquiryModal';
import { getMerchantTags } from '../../../../../services/clients';
import { formatCreatedDate, formatInquiryDate } from '../../../Rolex/utils';
import useVendorByKey from '../../../../../hooks/query/useVendorByKey';
import useClientInquiries from '../../../../../hooks/query/useClientInquiries';
import './styles.scss';

const Rolex = ({ client, onShareProducts }) => {
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [showEditModal, setShowEditModal] = useState(false);
  const [tagLookup, setTagLookup] = useState([]);

  const clientId = client?.client_id || client?.id;

  const { data: inquiriesResponse, isLoading, refetch } = useClientInquiries(clientId);
  const rawActive = inquiriesResponse?.data?.[0] || null;
  const rawClosed = inquiriesResponse?.closedInquiries || [];
  const loading = isLoading;

  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  // Resolve tag names/colors at render time so changes to tagLookup
  // don't trigger a refetch
  const activeInquiry = useMemo(() => {
    const inq = rawActive;
    if (!inq) return null;
    let resolvedTags;
    if (inq.tags && inq.tags.length > 0 && typeof inq.tags[0] === 'object') {
      resolvedTags = inq.tags
        .map(t => ({
          name: t.tagName || t.name,
          color: t.color || tagLookup.find(mt => mt.id === t.tagId)?.color || '#818181',
        }))
        .filter(t => t.name);
    } else {
      resolvedTags = (inq.tagIds || [])
        .map(id => {
          const lookup = tagLookup.find(t => t.id === id);
          return lookup ? { name: lookup.name, color: lookup.color || '#818181' } : null;
        })
        .filter(Boolean);
    }
    return {
      ...inq,
      createdDate: formatCreatedDate(inq.createdDt),
      createdByName: inq.createdByName || '',
      tags: resolvedTags,
      requestedProducts: inq.requestedProducts || [],
    };
  }, [rawActive, tagLookup]);

  const closedInquiries = useMemo(
    () =>
      rawClosed.map(inq => ({
        ...inq,
        createdDate: formatInquiryDate(inq.createdDt),
        closedDate: formatInquiryDate(inq.lastModifiedTs),
        associateName: inq.associateName || '',
      })),
    [rawClosed],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!activeInquiry && closedInquiries.length === 0) {
    return (
      <div style={{ padding: '26px', color: 'rgba(0,0,0,0.5)', fontSize: 14 }}>No Rolex inquiries for this client</div>
    );
  }

  const editInquiry = activeInquiry
    ? {
        ...activeInquiry,
        clientId,
        clientName: client?.name,
        associate: activeInquiry.associateName,
        models: activeInquiry.requestedProducts?.map(p => p.mpn) || [],
        inquiryDate: activeInquiry.inquiryDate,
      }
    : null;

  return (
    <div
      className="flex-col-left"
      style={{
        alignItems: 'stretch',
        maxHeight: '100%',
        gap: '36px',
        overflowX: 'hidden',
        paddingLeft: '26px',
        paddingRight: '26px',
      }}
    >
      {activeInquiry && (
        <>
          <InquiryHeader
            createdDate={activeInquiry.createdDate}
            createdBy={activeInquiry.createdByName}
            assignedTo={activeInquiry.associateName}
            status={activeInquiry.status}
            onEdit={() => setShowEditModal(true)}
          />
          <StorySection story={activeInquiry.story} />
          <TagsSection tags={activeInquiry.tags} />
          <RequestedProducts products={activeInquiry.requestedProducts} onShare={onShareProducts} />
        </>
      )}
      <ClosedInquiries inquiries={closedInquiries} />

      {editInquiry && (
        <AddRolexInquiryModal
          open={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            refetch();
          }}
          inquiry={editInquiry}
          vendorId={rolexVendorId}
        />
      )}
    </div>
  );
};

export default Rolex;
